import { useState, useEffect, useMemo, useRef } from 'react';
import { useI18n } from '../lib/i18n';
import { StatCard } from '../components/ui/StatCard';
import { PillTabs } from '../components/ui/PillTabs';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { HttpApiModal } from '../components/httpApi/HttpApiModal';
import { HttpApiTestModal } from '../components/httpApi/HttpApiTestModal';
import { HttpApiCallLogDrawer } from '../components/httpApi/HttpApiCallLogDrawer';
import { SheetFetchModal } from '../components/httpApi/SheetFetchModal';
import { HttpApiImportModal } from '../components/httpApi/HttpApiImportModal';
import {
  listHttpApis,
  deleteHttpApi,
  exportHttpApi,
  listSheetFetches,
  deleteSheetFetch,
  type HttpApiDoc,
  type SheetFetchDoc,
  type HttpMethod,
} from '../lib/httpApi';

type ActiveTab = 'apis' | 'sheets';

const METHOD_VARIANTS: Record<HttpMethod, 'info' | 'success' | 'warning' | 'brand' | 'danger'> = {
  GET: 'info',
  POST: 'success',
  PUT: 'warning',
  PATCH: 'brand',
  DELETE: 'danger',
};

export default function HttpApi() {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState<ActiveTab>('apis');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [httpApis, setHttpApis] = useState<HttpApiDoc[]>([]);
  const [sheetFetches, setSheetFetches] = useState<SheetFetchDoc[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals & Drawers states
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [editingApi, setEditingApi] = useState<HttpApiDoc | null>(null);

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testingApi, setTestingApi] = useState<HttpApiDoc | null>(null);

  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [loggingApi, setLoggingApi] = useState<HttpApiDoc | null>(null);

  const [importModalOpen, setImportModalOpen] = useState(false);

  const [sheetModalOpen, setSheetModalOpen] = useState(false);
  const [editingSheet, setEditingSheet] = useState<SheetFetchDoc | null>(null);

  const mountedRef = useRef(true);

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const [apisData, sheetsData] = await Promise.all([
        listHttpApis(),
        listSheetFetches(),
      ]);
      if (!mountedRef.current) return;
      setHttpApis(apisData);
      setSheetFetches(sheetsData);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err.message || t('httpApi.loadError'));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadData();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Stats calculations
  const stats = useMemo(() => {
    const totalApis = httpApis.length;
    const verifiedApis = httpApis.filter((a) => a.verified).length;
    const totalCalls = httpApis.reduce((sum, a) => sum + (a.stats?.totalCalls || 0), 0);
    const totalSheets = sheetFetches.length;
    return { totalApis, verifiedApis, totalCalls, totalSheets };
  }, [httpApis, sheetFetches]);

  // Filtered APIs
  const filteredApis = useMemo(() => {
    if (!searchQuery.trim()) return httpApis;
    const q = searchQuery.toLowerCase();
    return httpApis.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.url.toLowerCase().includes(q) ||
        a.method.toLowerCase().includes(q),
    );
  }, [httpApis, searchQuery]);

  // Actions
  const handleDeleteApi = async (api: HttpApiDoc) => {
    if (!(await confirm({
      title: t('common.areYouSure'),
      body: `${t('httpApi.deleteConfirm')} "${api.name}"?`,
      danger: true,
    }))) return;
    try {
      await deleteHttpApi(api.id);
      await loadData(true);
    } catch (err) {
      toast.error((err as Error).message || t('httpApi.deleteError'));
    }
  };

  const handleExportApi = async (api: HttpApiDoc) => {
    try {
      const config = await exportHttpApi(api.id);
      const blob = new Blob([JSON.stringify(config, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `http-api-${api.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error((err as Error).message || t('httpApi.exportError'));
    }
  };

  const handleDeleteSheet = async (fetchDoc: SheetFetchDoc) => {
    if (!(await confirm({
      title: t('common.areYouSure'),
      body: `${t('httpApi.sheet.deleteConfirm')} "${fetchDoc.name}"?`,
      danger: true,
    }))) return;
    try {
      await deleteSheetFetch(fetchDoc.id);
      await loadData(true);
    } catch (err) {
      toast.error((err as Error).message || t('httpApi.sheet.deleteError'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            {t('httpApi.title')}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('httpApi.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => loadData(true)}
            disabled={refreshing || loading}
          >
            {refreshing ? t('common.loading') : t('common.refresh')}
          </Button>

          {activeTab === 'apis' ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setImportModalOpen(true)}
              >
                {t('httpApi.importJson')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setEditingApi(null);
                  setApiModalOpen(true);
                }}
              >
                + {t('httpApi.createEndpoint')}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setEditingSheet(null);
                setSheetModalOpen(true);
              }}
            >
              + {t('httpApi.sheet.createTitle')}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* 4 StatCards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={t('httpApi.stats.totalEndpoints')}
          value={stats.totalApis}
        />
        <StatCard
          label={t('httpApi.stats.verifiedEndpoints')}
          value={stats.verifiedApis}
        />
        <StatCard
          label={t('httpApi.stats.totalCalls')}
          value={stats.totalCalls}
        />
        <StatCard
          label={t('httpApi.stats.sheetFetches')}
          value={stats.totalSheets}
        />
      </div>

      {/* PillTabs */}
      <PillTabs
        tabs={[
          { id: 'apis', label: t('httpApi.tabs.endpoints') },
          { id: 'sheets', label: t('httpApi.tabs.sheetFetches') },
        ]}
        activeId={activeTab}
        onChange={(k) => setActiveTab(k as ActiveTab)}
      />

      {/* Tab 1: HTTP APIs */}
      {activeTab === 'apis' && (
        <div className="space-y-4">
          {/* Search bar */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('httpApi.searchPlaceholder')}
              className="w-full sm:max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm outline-none focus:border-primary dark:border-slate-800 dark:bg-slate-900"
            />
          </div>

          {loading ? (
            <div className="py-16 text-center text-sm text-slate-400">
              {t('common.loading')}
            </div>
          ) : filteredApis.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-12 text-center dark:border-slate-800">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {t('httpApi.noEndpoints')}
              </p>
              <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                {t('httpApi.noEndpointsDesc')}
              </p>
              <Button
                variant="primary"
                size="sm"
                className="mt-4"
                onClick={() => {
                  setEditingApi(null);
                  setApiModalOpen(true);
                }}
              >
                + {t('httpApi.createEndpoint')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredApis.map((api) => {
                const total = api.stats?.totalCalls || 0;
                const success = api.stats?.success || 0;
                const successRate = total > 0 ? Math.round((success / total) * 100) : 0;

                return (
                  <div
                    key={api.id}
                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all hover:border-slate-300 dark:hover:border-slate-700"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={METHOD_VARIANTS[api.method]}>
                            {api.method}
                          </Badge>
                          <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                            {api.name}
                          </h3>
                          <Badge variant={api.verified ? 'success' : 'slate'}>
                            {api.verified
                              ? t('httpApi.status.verified')
                              : t('httpApi.status.unverified')}
                          </Badge>
                        </div>

                        <p className="text-xs font-mono text-slate-500 dark:text-slate-400 truncate">
                          {api.url}
                        </p>

                        <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap pt-1">
                          <span>
                            {t('httpApi.stats.totalCalls')}:{' '}
                            <strong className="text-slate-700 dark:text-slate-200">
                              {total}
                            </strong>
                          </span>
                          <span>•</span>
                          <span>
                            {t('httpApi.stats.successRate')}:{' '}
                            <strong className="text-emerald-600">
                              {total > 0 ? `${successRate}%` : '—'}
                            </strong>
                          </span>
                          <span>•</span>
                          <span>
                            {t('httpApi.headersCount')}:{' '}
                            {Object.keys(api.headers || {}).length}
                          </span>
                          <span>•</span>
                          <span>
                            {t('httpApi.mappingsCount')}:{' '}
                            {(api.responseMapping || []).length}
                          </span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1.5 flex-wrap self-start sm:self-center">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setTestingApi(api);
                            setTestModalOpen(true);
                          }}
                        >
                          {t('httpApi.testBtn')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setLoggingApi(api);
                            setLogsDrawerOpen(true);
                          }}
                        >
                          {t('httpApi.logsBtn')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportApi(api)}
                          title={t('httpApi.exportBtn')}
                        >
                          {t('httpApi.exportBtn')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingApi(api);
                            setApiModalOpen(true);
                          }}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteApi(api)}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Google Sheet Data Fetches */}
      {activeTab === 'sheets' && (
        <div className="space-y-4">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-400">
              {t('common.loading')}
            </div>
          ) : sheetFetches.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-12 text-center dark:border-slate-800">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {t('httpApi.sheet.noFetches')}
              </p>
              <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                {t('httpApi.sheet.noFetchesDesc')}
              </p>
              <Button
                variant="primary"
                size="sm"
                className="mt-4"
                onClick={() => {
                  setEditingSheet(null);
                  setSheetModalOpen(true);
                }}
              >
                + {t('httpApi.sheet.createTitle')}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <table className="w-full text-start text-xs sm:text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-900/50">
                  <tr>
                    <th className="p-3 ps-4">{t('httpApi.sheet.name')}</th>
                    <th className="p-3">{t('httpApi.sheet.sheetId')}</th>
                    <th className="p-3">{t('httpApi.sheet.tabName')}</th>
                    <th className="p-3">{t('httpApi.sheet.keyColumn')}</th>
                    <th className="p-3">{t('httpApi.sheet.updatedAt')}</th>
                    <th className="p-3 pe-4 text-end">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {sheetFetches.map((sf) => (
                    <tr key={sf.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                      <td className="p-3 ps-4 font-semibold text-slate-900 dark:text-white">
                        {sf.name}
                      </td>
                      <td className="p-3 font-mono text-xs text-slate-500 max-w-xs truncate">
                        {sf.sheetId}
                      </td>
                      <td className="p-3 text-slate-600 dark:text-slate-300">
                        {sf.tab || 'Sheet1'}
                      </td>
                      <td className="p-3">
                        <Badge variant="brand">{sf.keyColumn}</Badge>
                      </td>
                      <td className="p-3 text-slate-400 text-xs">
                        {new Date(sf.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="p-3 pe-4"><div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingSheet(sf);
                            setSheetModalOpen(true);
                          }}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSheet(sf)}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          {t('common.delete')}
                        </Button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals & Drawers */}
      <HttpApiModal
        open={apiModalOpen}
        onClose={() => setApiModalOpen(false)}
        api={editingApi}
        onSaved={() => loadData(true)}
      />

      <HttpApiTestModal
        open={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        api={testingApi}
        onVerified={() => loadData(true)}
      />

      <HttpApiCallLogDrawer
        open={logsDrawerOpen}
        onClose={() => setLogsDrawerOpen(false)}
        api={loggingApi}
      />

      <HttpApiImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={() => loadData(true)}
      />

      <SheetFetchModal
        open={sheetModalOpen}
        onClose={() => setSheetModalOpen(false)}
        fetchDoc={editingSheet}
        onSaved={() => loadData(true)}
      />
    </div>
  );
}
